import { useState } from "react"
import type { CSSProperties, FC } from "react"
import { Tooltip } from "antd"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import chevronDownIcon from "assets/icons/quotation-viewer/chevron-down.svg"
import { HIGHLIGHT_PALETTE } from "../../interactions/figureHighlight.constants"
import { compactTooltipStyles, getShadowTooltipContainer } from "../shadowTooltip"
import styles from "./ColorSwatchPicker.module.scss"

type Props = {
	/** `null` only ever occurs when `allowNoFill` is set — it is the "no fill" selection. */
	value: string | null
	onChange: (hex: string | null) => void
	colors?: readonly string[]
	/** Accessible name for the trigger button — distinguishes multiple pickers in the same toolbar
	 * (e.g. "Color" for stroke vs "Fill color"). */
	label: string
	/**
	 * Opt-in extra swatch (#1386 review: "the fill must have a no-fill option - like on the design")
	 * that selects `null` — the standard white-circle-with-a-red-diagonal-slash "none" affordance.
	 * Opt-IN rather than always-on because a highlight with no OUTLINE color makes no sense: only the
	 * fill picker passes it, and `null` can therefore never reach the stroke's non-nullable state.
	 */
	allowNoFill?: boolean
	/**
	 * How the trigger renders the current selection (#1386 review: "it is not clear which color
	 * circle is responsible for what"). `"solid"` is a filled disc (the fill picker); `"ring"` is a
	 * hollow ring — the outline drawn in `value`, transparent centre — which is how the design shows
	 * the STROKE picker, so the two controls read as outline-vs-fill at a glance instead of as two
	 * identical discs.
	 */
	previewVariant?: "solid" | "ring"
}

const POPOVER_WIDTH = 170
const SWATCH_SIZE = 24
const SWATCH_GAP = 8

const popoverStyle: CSSProperties = {
	display: "flex",
	flexWrap: "wrap",
	width: POPOVER_WIDTH,
	gap: SWATCH_GAP,
	padding: SWATCH_GAP,
	background: "#fff",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	borderRadius: 8,
	boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
}

const swatchStyle = (hex: string, selected: boolean): CSSProperties => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: SWATCH_SIZE,
	height: SWATCH_SIZE,
	borderRadius: "50%",
	border: "1px solid var(--clr-lightGray, #dee0eb)",
	cursor: "pointer",
	padding: 0,
	backgroundColor: hex,
	boxShadow: selected ? "0 0 0 2px #fff inset, 0 0 0 4px var(--clr-main, #0047ff)" : undefined,
})

/**
 * The "none" affordance: a red diagonal slash across an otherwise blank (white) circle. Drawn as an
 * inline SVG rather than a CSS gradient because the popover portals to `document.body`, outside this
 * component's injected shadow stylesheet — an inline SVG travels with the subtree wherever the popup
 * lands (the same reason every style in this file is inline). The slash colour comes from the
 * `--clr-warning` token, with the literal as a fallback for the same portal reason.
 */
const NoFillMark: FC = () => (
	<svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
		<line
			x1={5}
			y1={19}
			x2={19}
			y2={5}
			stroke="var(--clr-warning, #ec404c)"
			strokeWidth={2}
			strokeLinecap="round"
		/>
	</svg>
)

/**
 * Small reusable color-swatch dropdown (REQ-400): a circular preview of the current color + a
 * chevron trigger, opening a floating 6-per-row grid of hex swatches directly below it (Figma
 * "Polygon / Color / Opened", node 19019:673318 — a compact ~170×92px popover, not inline content).
 * No generic color/swatch picker existed under `components/ui/` to extend (checked first, per
 * AGENTS.md) — the palette itself is fixed product data (figureHighlight.constants), not a design
 * token, so it's passed in rather than hardcoded here, keeping this component reusable for a future
 * non-highlight palette too.
 *
 * Two opt-in variations exist for the #1386 review, both driven by props rather than a second
 * component: `allowNoFill` adds the slashed "none" swatch (fill only), and `previewVariant="ring"`
 * draws the trigger as a hollow ring instead of a solid disc (stroke only). Together they are what
 * makes "which circle is the outline and which is the fill" answerable at a glance.
 *
 * Renders its popover via `popupRender` with fully INLINE styles, and does NOT pass
 * `renderInPlace` — unlike this codebase's other `renderInPlace` uses (all inside a SIDEBAR panel's
 * own separate shadow-mounted root, where portaling to `document.body` would escape that panel's
 * injected stylesheet entirely), this component lives in the HEADER, where every other dropdown
 * (Shapes, the burger/view-controls menus) already portals to `document.body` successfully. Reusing
 * `renderInPlace` here was the actual bug (#1323 review, reproduced live via screenshot): AntD's
 * popup only floats via `position: absolute` when it can compute against a normal container —
 * `getPopupContainer: (trigger) => trigger.parentElement` handed it a small in-flow header element
 * instead, and the popup rendered as an ordinary block child of that element rather than floating,
 * pushing the header/document layout down instead of overlaying it. Matching the header's OTHER
 * dropdowns (no `renderInPlace`) fixes the floating behavior; going through `popupRender` with
 * inline styles (rather than the CSS-Module classes the grid used before) means the popup's own
 * layout doesn't depend on whichever stylesheet happens to be reachable from wherever it portals to.
 */
export const ColorSwatchPicker: FC<Props> = ({
	value,
	onChange,
	colors = HIGHLIGHT_PALETTE,
	label,
	allowNoFill = false,
	previewVariant = "solid",
}) => {
	const [open, setOpen] = useState(false)
	const isNoFill = value === null
	// A ring reads as "outline" only if the ring itself is the chosen colour and the centre is
	// see-through; a no-fill selection instead shows the slashed circle, so the trigger says "nothing
	// will be painted here" rather than showing a blank white disc.
	const previewStyle: CSSProperties = isNoFill
		? { backgroundColor: "#fff" }
		: previewVariant === "ring"
			? { borderColor: value ?? undefined, backgroundColor: "transparent" }
			: { backgroundColor: value ?? undefined }

	return (
		<Dropdown
			items={[]}
			placement="bottomLeft"
			open={open}
			onOpenChange={setOpen}
			trigger={["click"]}
			overlayStyle={{ zIndex: 1500 }}
			popupRender={() => (
				<div style={popoverStyle}>
					{colors.map((hex) => (
						<button
							key={hex}
							type="button"
							style={swatchStyle(hex, hex.toLowerCase() === value?.toLowerCase())}
							aria-label={hex}
							title={hex}
							onClick={() => {
								onChange(hex)
								setOpen(false)
							}}
						/>
					))}
					{allowNoFill ? (
						<button
							type="button"
							style={swatchStyle("#fff", isNoFill)}
							aria-label="No fill"
							title="No fill"
							onClick={() => {
								onChange(null)
								setOpen(false)
							}}
						>
							<NoFillMark />
						</button>
					) : null}
				</div>
			)}
		>
			{/* No onClick here — with trigger={["click"]}, AntD's Dropdown already toggles `open`
			    itself (via onOpenChange) when this trigger element is clicked. An extra manual
			    setOpen on the same click landed in the same React batch as AntD's own and flipped
			    it straight back — the popover could never actually open (#1323 review, reproduced
			    live). onOpenChange is the single source of truth for this control. */}
			<Tooltip title={label} placement="bottom" getPopupContainer={getShadowTooltipContainer} styles={compactTooltipStyles}>
				<button type="button" className={styles.trigger} aria-label={label}>
					<span
						className={previewVariant === "ring" && !isNoFill ? `${styles.preview} ${styles.previewRing}` : styles.preview}
						style={previewStyle}
					>
						{isNoFill ? <NoFillMark /> : null}
					</span>
					<img className={styles.chevron} src={chevronDownIcon} alt="" />
				</button>
			</Tooltip>
		</Dropdown>
	)
}
